<template>
	<div class="content-page content-page--transactions">
		<div class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">
					Transactions
					<span class="content-page__title--detail">
						{{ dcsIntegration }}
					</span>
				</h2>
			</div>
		</div>
		<div class="results-toolbar results-toolbar--variant-three">
			<TransactionFilters
				v-model="filters"
				:defaultFilters="defaultFilters"
				:customerOptions="customerItems || []"
				:bodOptions="versionItems || []"
			/>
			<div
				class="results-toolbar__counter"
				v-if="!loading && resultCounter"
			>
				<ResultCounter :counter="resultCounter" />
			</div>
		</div>

		<Spinner :processor="{ loading: loading }" />
		<ScrollContainer v-if="!loading && hasResults" data-test="pageContent">
			<Table
				:sourceObject="tableSource"
				@change="updatedPagination"
				@sort="updateDirection"
				:sortOptions="handleSort"
				:isArrowUp="true"
			>
				<template #body-icon="{ body }">
					<i
						v-bind:style="body.iconStyle"
						:class="`icon icon-${body.icon}`"
					/>
				</template>
				<template #body-timestamp="{ body }">
					{{ fullDateString(body.timestamp) }}
				</template>
				<template #body-requestUri="{ body }">
					<div>
						<a
							class="btn btn--icon-only-circle"
							href="#"
							v-if="body.requestUri"
							@click="downloadFile(body.requestUri)"
							><i class="icon icon-export"></i> Export</a
						>
						<span v-else></span>
					</div>
				</template>
				<template #body-responseUri="{ body }">
					<div>
						<a
							class="btn btn--icon-only-circle"
							href="#"
							v-if="body.responseUri"
							@click="downloadFile(body.responseUri)"
							><i class="icon icon-export"></i> Export</a
						>
						<span v-else></span>
					</div>
				</template>
			</Table>
		</ScrollContainer>
		<NoResults v-if="!hasResults && !loading" ref="noResults" />
	</div>
</template>

<script>
import TransactionFilters from '@/sections/integration/transactions/transactions-page-filters.vue';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import BlobService from '@/services/blob-service';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import NoResults from '@/site-components/no-results/no-results';
import Spinner from '@/components/visual/spinner/spinner';
import Table from '@/components/shells/table/table';
import ResultCounter from '@/site-components/result-counter/result-counter';

// mixins
import DateTimeMixin from '@/mixins/datetime-mixin';
import CustomerOptionsMixins from '@/mixins/customers/customer-options';
import cardActions from '../integrations/card-actions';

// utils
import headerOptions from './header-options';
import PageKeyNames from '@/utils/page-key-names';
import {
	removeObjectEmptyValues,
	removePropertiesWithSameValues
} from '@/utils/object';
// vuex
import { mapState, mapActions, mapGetters } from 'vuex';

// libraries
import { formatDate } from '@/utils/datetime';
import { sub } from 'date-fns';
import _ from 'lodash';

//constants
import {
	THIRD_PARTY,
	INTEGRATIONS
} from '@/sections/integration/integrations-mutation-types';

export default {
	name: 'IntegrationTransactions',
	mixins: [CustomerOptionsMixins, DateTimeMixin, cardActions],
	components: {
		TransactionFilters,
		Breadcrumbs,
		Spinner,
		Table,
		ScrollContainer,
		NoResults,
		ResultCounter
	},
	props: {
		partyId: {
			type: String,
			required: true
		},
		dealershipGroupGuid: {
			type: String
		},
		companyId: {
			type: Number
		},
		maxDateTime: String,
		minDateTime: String,
		bodType: String,
		level: String,
		page: Number,
		countPerPage: Number,
		orderBy: String,
		orderDirection: String,
		integrationId: {
			type: Number
		},
		thirdPartyId: { type: Number },
		siteBodId: { type: Number }
	},
	data() {
		return {
			loading: true,
			filters: {
				partyId: this.partyId,
				integrationId: this.integrationId,
				thirdPartyId: this.thirdPartyId,
				minDatetime: this.minDatetime,
				maxDatetime: this.maxDatetime,
				dealershipGroupGuid: this.dealershipGroupGuid,
				bodType: this.bodType,
				level: this.level,
				companyId: this.companyId,
				page: this.page,
				countPerPage: this.countPerPage,
				orderBy: this.orderBy,
				orderDirection: this.orderDirection
			},
			defaultFilters: {
				partyId: this.partyId,
				integrationId: this.integrationId,
				thirdPartyId: this.thirdPartyId,
				minDatetime: formatDate(
					new Date(sub(new Date(), { weeks: 1 }))
				),
				maxDatetime: formatDate(new Date()),
				dealershipGroupGuid: null,
				bodType: null,
				level: 'INFO',
				companyId: 0,
				page: 1,
				countPerPage: 10,
				orderBy: 'timestamp',
				orderDirection: 'desc'
			},

			tableSource: {}
		};
	},
	computed: {
		...mapState('historyLogs', ['transactions']),
		...mapState('integrations', [
			'versionItems',
			THIRD_PARTY,
			INTEGRATIONS
		]),
		...mapGetters('options', ['customerItems']),
		hasResults() {
			return this.transactions.items?.length > 0;
		},
		resultCounter() {
			if (!this.transactions.items) {
				return '';
			}
			return (
				`${this.transactions.items?.length} of ${this.transactions.totalCount}` ||
				0
			);
		},
		formatedSecurevueTransactions() {
			let transactionsWithNewFormat = { ...this.transactions };

			if (!transactionsWithNewFormat.items) {
				return {};
			}

			transactionsWithNewFormat.items?.forEach(item => {
				item.customerName = this.getOptionsCustomerName(
					item.dealershipGroupGuid
				);
				item.dealerNumberAndCompany = `${
					item.dealerNumberId
				} (${item.companyId.toString().padStart(2, '0')})`;

				if ((item.level || '').toLowerCase() === 'error') {
					item.icon = 'warning';
					item.iconStyle = {
						color: 'red'
					};
				} else {
					item.icon = 'info';
					item.iconStyle = {
						color: 'blue'
					};
				}
			});
			return transactionsWithNewFormat;
		},
		handleSort() {
			return {
				currentSort: this.filters.orderBy,
				currentSortDir: this.filters.orderDirection
			};
		},
		urlNames() {
			let integrationsNameRoute = 'Integrations Third Parties';
			let routes = [
				{
					name: 'Third Party Manager',
					data: { path: '/integrations/thirdparties' }
				},
				{ name: 'Transactions' }
			];

			if (!_.isEmpty(this[INTEGRATIONS]) && this.integrationId) {
				const integration = this[INTEGRATIONS].items.find(
					integration => {
						return integration.id === this.integrationId;
					}
				);
				integrationsNameRoute = integration
					? `${integration.dealerNumberId}`
					: integrationsNameRoute;
				routes.splice(1, 0, {
					url: `/integrations/thirdparties/${this.thirdPartyId}/integrations`,
					name: integrationsNameRoute
				});

				if (this.siteBodId) {
					routes.splice(2, 0, {
						url: `/integrations/thirdparties/${this.thirdPartyId}/integrations/${this.integrationId}/sitebods`,
						name: `${this.bodType}`
					});
				}
			}

			return routes;
		},
		dcsIntegration() {
			if (!_.isEmpty(this[THIRD_PARTY])) {
				return _.get(this[THIRD_PARTY], 'dcsIntegration');
			}
			return '';
		}
	},
	async created() {
		this.mainNavActions = this.actions;
		this.tableSource.headers = headerOptions;

		try {
			await this.getCustomers();
			await this.getVersionItems();
		} catch (err) {
			console.log(err);
		}
	},
	mounted() {
		this.$store.commit('setCurrentPage', PageKeyNames.TRANSACTIONS);
	},
	destroyed() {
		this.$store.commit('setCurrentPage', null);
	},
	methods: {
		...mapActions('historyLogs', ['getTransactions']),
		...mapActions('options', ['getCustomers']),
		...mapActions('integrations', ['getVersionItems']),
		async downloadFile(uri) {
			await BlobService.DownloadSecurevueTransaction(
				uri,
				'application/xml'
			);
		},
		async fetchData() {
			this.loading = true;

			const filteredQuery = _.cloneDeep(this.filters);

			filteredQuery.recordStart = this.filters.page;
			filteredQuery.maxCount = this.filters.countPerPage;
			filteredQuery.dealershipGroupGuid;
			delete filteredQuery.page;
			delete filteredQuery.countPerPage;

			if (!filteredQuery.integrationId) {
				delete filteredQuery.integrationId;
			}
			if (!filteredQuery.thirdPartyId) {
				delete filteredQuery.thirdPartyId;
			}
			if (this.dcsIntegration === 'Xtime') {
				delete filteredQuery.partyId;
				Object.assign(filteredQuery, { logType: this.dcsIntegration });
			}

			try {
				await this.getTransactions(filteredQuery);

				this.tableSource.source = this.formatedSecurevueTransactions;
			} finally {
				this.loading = false;
			}
		},
		updateRoute(queries) {
			let path = `/integrations/thirdparties/${this.partyId}`;
			if (this.thirdPartyId && this.integrationId) {
				path = `${path}/${this.thirdPartyId}/${this.integrationId}`;
				if (this.siteBodId) {
					path = `${path}/${this.siteBodId}`;
				}
			}

			let queryParameters = removePropertiesWithSameValues(
				queries,
				this.defaultFilters
			);
			path = path + '/transactions';

			queries = removeObjectEmptyValues(queries);

			this.$router
				.push({
					path: path,
					query: queryParameters
				})
				.catch(() => {});
		},
		updateDirection(sortValue) {
			this.filters.orderDirection = sortValue.sortDir;
		},
		updatedPagination(value) {
			this.filters.page = value.page;
			this.filters.countPerPage = value.countPerPage;
		}
	},
	watch: {
		filters: {
			deep: true,
			handler(newValue) {
				this.updateRoute(newValue);
				this.fetchData();
			}
		},
		'$route.query': {
			immediate: true,
			handler(query) {
				let newQuery = _.cloneDeep(query);
				if (newQuery.page) {
					newQuery.page = this.page;
				}
				if (newQuery.countPerPage) {
					const availableCountPerPage = [10, 20, 30, 50, 100];

					if (availableCountPerPage.includes(this.countPerPage)) {
						newQuery.countPerPage = this.countPerPage;
					} else {
						newQuery.countPerPage = this.defaultFilters.countPerPage;
					}
				}
				let newfilters = { ...this.defaultFilters, ...newQuery };
				if (newfilters.companyId) {
					newfilters.companyId = parseInt(newfilters.companyId);
				}
				if (!_.isEqual(newfilters, this.filters)) {
					this.filters = _.cloneDeep(newfilters);
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.results-toolbar {
	width: 100%;
}
</style>
